import React, { useState, useEffect } from "react";

/** Fallback labels cycled when the caller supplies no live activity text. */
const DEFAULT_CYCLE = ["Working on it", "Still working"];

/** The per-variant styling the indicator needs, gathered in one place. */
interface IndicatorStyles {
  /** Outer wrapper: card chrome in the expanded variant, bare box in compact. */
  container: string;
  /** Label typography. */
  text: string;
  /**
   * Height of the label wrapper. The cross-fading labels are absolutely
   * positioned, so the wrapper needs an explicit height — and it must match the
   * text's own line box, or the label sits high inside a too-tall box and looks
   * vertically off from its siblings. text-xs → 1rem (h-4); text-sm → 1.25rem
   * (h-5).
   */
  labelBox: string;
  /** A single bouncing dot. */
  dot: string;
  /**
   * Element to render the wrappers as.
   *
   * Compact renders spans because it's used inline inside other rows —
   * including `ToolChevron`'s `<button>` — where a block-level element is
   * invalid nesting (React logs a hydration warning and browsers lay it out
   * unpredictably). Display comes from the classes either way, so the element
   * choice costs nothing visually.
   */
  box: "span" | "div";
}

/** Full-size variant: a standalone card in the chat transcript. */
const EXPANDED_STYLES: IndicatorStyles = {
  container: "bg-gray-50 dark:bg-[#0F141A] rounded-lg px-4 py-4 w-full",
  text: "text-sm font-medium text-muted-foreground",
  labelBox: "h-5",
  dot: "working-dot h-1.5 w-1.5 rounded-full bg-blue-600 dark:bg-blue-400 animate-bounce",
  box: "div",
};

/**
 * Compact variant: sits inline in a `ToolChevron` header. Drops the card chrome
 * and fills its flex parent so the indicator inherits that row's baseline
 * instead of forming its own box.
 */
const COMPACT_STYLES: IndicatorStyles = {
  container: "min-w-0 w-full",
  text: "text-xs font-normal text-muted-foreground",
  labelBox: "h-4",
  dot: "working-dot h-1 w-1 rounded-full bg-blue-600 dark:bg-blue-400 animate-bounce",
  box: "span",
};

/** Picks the styling variant for the current size. */
function getIndicatorStyles(isCompact: boolean): IndicatorStyles {
  return isCompact ? COMPACT_STYLES : EXPANDED_STYLES;
}

interface WorkingIndicatorProps {
  /**
   * Whether to alternate the label with `alternateText`.
   *
   * Retained for the existing call sites. When false the label is pinned to
   * "Working on it"; the animation still runs, so the indicator always reads as
   * active.
   */
  showThinkingHint?: boolean;
  /**
   * Live activity label to alternate with "Working on it" — e.g. the current
   * tool summary ("Reading summary.md", 'Searching: "…"').
   *
   * When omitted the component falls back to `DEFAULT_CYCLE`, so it never
   * references the Thinking tab (hidden under `thinkingChevron`).
   */
  alternateText?: string;
  /** Interval in ms between text alternations. Default: 3000 */
  alternateInterval?: number;
  /** Render at a smaller scale, for use inside a collapsed chevron header. */
  compact?: boolean;
}

/**
 * Animated loading indicator shown while the agent is working.
 *
 * Three staggered bouncing dots with an alternating label. The dots animate
 * unconditionally — the indicator's job is to prove work is ongoing, so it must
 * never render as a static string. They hold still under
 * `prefers-reduced-motion` (see the `.working-dot` rule in `index.css`).
 *
 * Long labels (a verbose search query, a deep file path) are truncated with an
 * ellipsis rather than allowed to widen the row: this renders inside a
 * fixed-width chat panel, and an unbounded label produces a horizontal
 * scrollbar on the whole panel.
 */
const WorkingIndicator = ({
  showThinkingHint = true,
  alternateText,
  alternateInterval = 3000,
  compact = false,
}: WorkingIndicatorProps) => {
  const [showAlternateText, setShowAlternateText] = useState(false);

  // Alternate only when there is a distinct second label worth showing.
  const secondLabel =
    alternateText && alternateText !== DEFAULT_CYCLE[0]
      ? alternateText
      : DEFAULT_CYCLE[1];
  const shouldAlternate = showThinkingHint;

  useEffect(() => {
    if (!shouldAlternate) return;
    setShowAlternateText(false);
    const interval = setInterval(() => {
      setShowAlternateText((prev) => !prev);
    }, alternateInterval);
    return () => clearInterval(interval);
  }, [shouldAlternate, alternateInterval, secondLabel]);

  const styles = getIndicatorStyles(compact);
  const Box = styles.box;

  return (
    <Box className={styles.container}>
      {/* min-w-0 lets the label shrink so `truncate` can engage instead of the
          row growing past the panel width. */}
      <Box className="flex items-center gap-2 min-w-0">
        {shouldAlternate ? (
          // flex-1 + min-w-0 caps the wrapper's width at the row so `truncate`
          // engages; `labelBox` matches its height to the text line box.
          <span
            className={`relative flex-1 min-w-0 ${styles.text} ${styles.labelBox} overflow-hidden`}
          >
            <span
              className={`absolute inset-x-0 top-0 block truncate transition-all duration-500 ${
                showAlternateText
                  ? "opacity-0 -translate-y-2"
                  : "opacity-100 translate-y-0"
              }`}
            >
              Working on it
            </span>
            <span
              className={`absolute inset-x-0 top-0 block truncate transition-all duration-500 ${
                showAlternateText
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-2"
              }`}
              title={secondLabel}
            >
              {secondLabel}
            </span>
          </span>
        ) : (
          <span className={`${styles.text} truncate min-w-0`}>
            Working on it
          </span>
        )}
        {/* Always animating — this is the "something is happening" signal.
            No vertical nudge: the parent's `items-center` already aligns the
            dots with the label, and an offset here reads as misalignment
            against neighbouring rows. */}
        <span
          className="inline-flex items-center gap-1 flex-shrink-0"
          aria-hidden="true"
        >
          <span className={styles.dot} style={{ animationDelay: "0ms" }} />
          <span className={styles.dot} style={{ animationDelay: "150ms" }} />
          <span className={styles.dot} style={{ animationDelay: "300ms" }} />
        </span>
      </Box>
    </Box>
  );
};

export default WorkingIndicator;
